import { Immutability, UUID } from "@bitory-gmbh/nidavellir";
import { Document, Artefact } from "@bitory-gmbh/bsm";
import {
  Address,
  newAddress,
} from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import { Representative } from "@/services/backend/representatives/types/representative";
import {
  newStructural,
  StructuralAssessment,
} from "@/services/backend/htz/inspection/types/structural-assessment";
import {
  newVisual,
  VisualAssessment,
} from "@/services/backend/htz/inspection/types/visual-assessment";
import {
  MicrobialAssessment,
  newMicrobial,
} from "@/services/backend/htz/inspection/types/microbial-assessment";
import { newPhoto, Photo } from "@/services/backend/htz/inspection/types/photo";
import { Process } from "@/services/backend/processes/types/process";
import { v4 } from "uuid";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { Item } from "@/services/backend/htz/position-catalog/types/position-catalog";
import { Kind } from "@/services/backend/samples/types/kind";
import { EntityId } from "@/shared/types/entity-id";
import {
  containsSampleId,
  Delivery,
} from "@/services/backend/samples/types/delivery";
import { Common } from "@/services/backend/documents/types/common";
import { State } from "@/services/backend/htz/work-order/components/work-order-state";

export const HTZ_WORK_ORDER_NAMESPACE = "core.htz.domain.workorder.WorkOrder";

export interface WorkOrder extends Document, Artefact, Immutability, Common {
  state: State;
  executingBranchId: string;
  beginDate: string | null;
  endDate: string | null;
  teamLeader: string;
  internalComment: string;
  address: Address;
  contact: Contact;
  representative: Representative;
}

export function newWorkOrder(
  process: Process,
  branchId: string,
  serviceObjectId: string,
): WorkOrder {
  return {
    id: v4(),
    documentKind: GSMDocumentKind.WorkOrder,
    processId: process.id,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    immutable: false,
    immutabilityReason: "",
    immutabilitySetAt: undefined,
    immutabilitySetBy: "",
    companyId: process.companyId,
    branchId,
    customerId: process.customerId!,
    serviceObjectId,
    state: State.Planning,
    executingBranchId: branchId,
    beginDate: null,
    endDate: null,
    teamLeader: "",
    internalComment: "",
    address: newAddress(),
    contact: {
      salutation: "",
      title: "",
      name: "",
      function: "",
      email: "",
      phone: "",
      mobilephone: "",
    } as Contact,
    representative: {
      name: "",
      email: "",
      phone: "",
      mobilephone: "",
    } as Representative,
  };
}

export function updateState(workOrder: WorkOrder, state: State): WorkOrder {
  if (workOrder.immutable) {
    return workOrder;
  }

  return {
    ...workOrder,
    state,
  };
}

export function updateExecutingBranch(
  workOrder: WorkOrder,
  executingBranchId: string,
): WorkOrder {
  if (workOrder.immutable) {
    return workOrder;
  }

  return {
    ...workOrder,
    executingBranchId,
  };
}

export function updateBeginDate(
  workOrder: WorkOrder,
  beginDate: string | null,
): WorkOrder {
  if (workOrder.immutable) {
    return workOrder;
  }

  return {
    ...workOrder,
    beginDate,
  };
}

export function updateEndDate(
  workOrder: WorkOrder,
  endDate: string | null,
): WorkOrder {
  if (workOrder.immutable) {
    return workOrder;
  }

  return {
    ...workOrder,
    endDate,
  };
}

export function updateTeamLeader(
  workOrder: WorkOrder,
  teamLeader: string,
): WorkOrder {
  if (workOrder.immutable) {
    return workOrder;
  }

  return {
    ...workOrder,
    teamLeader,
  };
}

export function updateInternalComment(
  workOrder: WorkOrder,
  internalComment: string,
): WorkOrder {
  if (workOrder.immutable) {
    return workOrder;
  }

  return {
    ...workOrder,
    internalComment,
  };
}

export function updateAddress(
  workOrder: WorkOrder,
  address: Address,
): WorkOrder {
  if (workOrder.immutable) {
    return workOrder;
  }

  return {
    ...workOrder,
    address,
  };
}

export function updateContact(
  workOrder: WorkOrder,
  contact: Contact,
): WorkOrder {
  if (workOrder.immutable) {
    return workOrder;
  }

  return {
    ...workOrder,
    contact,
  };
}

export function updateRepresentative(
  workOrder: WorkOrder,
  representative: Representative,
): WorkOrder {
  if (workOrder.immutable) {
    return workOrder;
  }

  return {
    ...workOrder,
    representative,
  };
}

export function assessmentsWithoutDelivery(
  sampleDeliveries: Delivery[],
  positions: Position[],
): MicrobialAssessment[] {
  return positions
    .map((pos) => pos.microbialAssessments ?? [])
    .flat()
    .filter((a) =>
      sampleDeliveries.every((delivery) => {
        if (delivery.cancelled) {
          return true;
        }
        return !containsSampleId(delivery, String(a.sampleId));
      }),
    );
}

export interface Position {
  id: string;
  workOrderId: string;
  companyId: UUID;
  branchId: UUID;
  number: number;
  completed: boolean;
  ahuId: EntityId;
  ahuDataId: EntityId;
  title: string;
  description: string;
  unitCount: number;
  unitPrice: number;
  totalPrice: number;
  unitHours: number;
  totalHours: number;
  optional: boolean;
  structuralInspectionRequired: boolean;
  structuralAssessments: StructuralAssessment[];
  visualInspectionRequired: boolean;
  visualAssessments: VisualAssessment[];
  microbialInspectionRequired: boolean;
  microbialAssessments: MicrobialAssessment[];
  photoDocumentationRequired: boolean;
  photos: Photo[];
}

export function newFromCatalog(
  catPosition: Item,
  workOrderId: string,
  companyId: UUID,
  branchId: UUID,
): Position {
  const {
    structuralInspectionRequired,
    visualInspectionRequired,
    microbiologicalInspectionRequired,
    photoDocumentationRequired,
  } = catPosition;

  return {
    id: v4(),
    workOrderId,
    companyId,
    branchId,
    number: 0, // set by backend
    completed: false,
    ahuId: null,
    ahuDataId: null,
    title: catPosition.copyTitle ? catPosition.title : "",
    description: catPosition.copyDescription ? catPosition.description : "",
    unitCount: 0,
    unitPrice: catPosition.copyUnitPrice ? catPosition.unitPrice : 0,
    totalPrice: 0,
    unitHours: catPosition.copyUnitHours ? catPosition.unitHours : 0,
    totalHours: 0,
    optional: false,
    // The inspections are initialized with null, since it may
    // not be known to which air handling unit the work order position
    // will belong. This seems odd, but is the case in the domain, since
    // a work order may be created based on an order in which the
    // actual air handling units where still unknown.
    structuralInspectionRequired,
    structuralAssessments: [],
    visualInspectionRequired,
    visualAssessments: [],
    microbialInspectionRequired: microbiologicalInspectionRequired,
    microbialAssessments: [],
    photoDocumentationRequired,
    photos: [],
  };
}

function assertAirHandlingUnitAssigned(pos: Position) {
  if (pos.ahuId === null || pos.ahuDataId === null) {
    throw new Error(
      `no air handling unit or air handling unit data assigned to position. id: ${pos.id}`,
    );
  }
}

export function updateAhu(
  position: Position,
  ahuId: EntityId,
  ahuDataId: EntityId,
): Position {
  if (ahuId === null) {
    // Inspections are completely dependent on the air handling unit
    // assigned. Changing the ahu, requires removing the inspections.
    return {
      ...position,
      ahuId: null,
      ahuDataId: null,
      structuralAssessments: [],
      visualAssessments: [],
      microbialAssessments: [],
      photos: [],
    };
  }

  // Changing the ahu requires a new inspection, since the assessments
  // are coupled the ahu components.
  return {
    ...position,
    ahuId,
    ahuDataId,
    structuralAssessments: [],
    visualAssessments: [],
    microbialAssessments: [],
    photos: [],
  };
}

export function updateDescription(
  position: Position,
  description: string,
): Position {
  return {
    ...position,
    description,
  };
}

export function updateUnitCount(
  position: Position,
  unitCount: number,
): Position {
  return {
    ...position,
    unitCount,
    totalPrice: unitCount * position.unitPrice,
    totalHours: unitCount * position.unitHours,
  };
}

export function updateUnitPrice(
  position: Position,
  unitPrice: number,
): Position {
  return {
    ...position,
    unitPrice,
    totalPrice: unitPrice * position.unitCount,
  };
}

export function updateUnitHours(
  position: Position,
  unitHours: number,
): Position {
  return {
    ...position,
    unitHours,
    totalHours: unitHours * position.unitCount,
  };
}

export function updateStructuralInspectionRequired(
  position: Position,
  value: boolean,
): Position {
  return {
    ...position,
    structuralInspectionRequired: value,
  };
}

export function addStructuralAssessment(
  position: Position,
  componentId: string,
  componentDataId: string,
  assessmentId: string,
): Position {
  assertAirHandlingUnitAssigned(position);

  const assessment = newStructural(assessmentId, position.ahuId!, componentId);

  return {
    ...position,
    structuralAssessments: [
      ...(position.structuralAssessments ?? []),
      assessment,
    ],
  };
}

export function updateStructuralAssessment(
  position: Position,
  assessment: StructuralAssessment,
): Position {
  assertAirHandlingUnitAssigned(position);

  return {
    ...position,
    structuralAssessments: position.structuralAssessments.map((a) =>
      a.id === assessment.id ? assessment : a,
    ),
  };
}

export function deleteStructuralAssessment(
  position: Position,
  assessmentId: string,
): Position {
  assertAirHandlingUnitAssigned(position);

  return {
    ...position,
    structuralAssessments: position.structuralAssessments.filter(
      (a) => a.id !== assessmentId,
    ),
  };
}

export function updateVisualInspectionRequired(
  position: Position,
  value: boolean,
): Position {
  return {
    ...position,
    visualInspectionRequired: value,
  };
}

export function addVisualAssessment(
  position: Position,
  componentId: string,
  componentDataId: string,
  assessmentId: string,
): Position {
  assertAirHandlingUnitAssigned(position);

  const assessment = newVisual(assessmentId, position.ahuId!, componentId);

  return {
    ...position,
    visualAssessments: [...(position.visualAssessments ?? []), assessment],
  };
}

export function updateVisualAssessment(
  position: Position,
  assessment: VisualAssessment,
): Position {
  assertAirHandlingUnitAssigned(position);

  return {
    ...position,
    visualAssessments: position.visualAssessments.map((a) =>
      a.id === assessment.id ? assessment : a,
    ),
  };
}

export function deleteVisualAssessment(
  position: Position,
  assessmentId: string,
): Position {
  assertAirHandlingUnitAssigned(position);

  return {
    ...position,
    visualAssessments: position.visualAssessments.filter(
      (a) => a.id !== assessmentId,
    ),
  };
}

export function updateMicrobialInspectionRequired(
  position: Position,
  value: boolean,
): Position {
  return {
    ...position,
    microbialInspectionRequired: value,
  };
}

export function addMicrobialAssessment(
  position: Position,
  componentId: string,
  componentDataId: string,
  assessmentId: string,
  kind: Kind,
  sampleId: EntityId,
): Position {
  assertAirHandlingUnitAssigned(position);

  const assessment = newMicrobial(
    assessmentId,
    position.ahuId!,
    componentId,
    kind,
    sampleId,
  );

  return {
    ...position,
    microbialAssessments: [
      ...(position.microbialAssessments ?? []),
      assessment,
    ],
  };
}

export function deleteMicrobialAssessment(
  position: Position,
  assessmentId: string,
): Position {
  assertAirHandlingUnitAssigned(position);

  return {
    ...position,
    microbialAssessments: position.microbialAssessments.filter(
      (a) => a.id !== assessmentId,
    ),
  };
}

export function updatePhotoDocumentationRequired(
  position: Position,
  value: boolean,
): Position {
  return {
    ...position,
    photoDocumentationRequired: value,
  };
}

export function addPhoto(
  position: Position,
  componentId: string,
  componentDataId: string,
  photoId: string,
): Position {
  assertAirHandlingUnitAssigned(position);

  const photo = newPhoto(photoId, position.ahuId!, componentId);

  return {
    ...position,
    photos: [...(position.photos ?? []), photo],
  };
}

export function updatePhoto(position: Position, photo: Photo): Position {
  assertAirHandlingUnitAssigned(position);

  return {
    ...position,
    photos: position.photos.map((p) => (p.id === photo.id ? photo : p)),
  };
}

export function deletePhoto(position: Position, photoId: string): Position {
  assertAirHandlingUnitAssigned(position);

  return {
    ...position,
    photos: position.photos.filter((p) => p.id !== photoId),
  };
}
